import {
  ChangeLog,
  Response,
  apiAbortController,
} from '../../../../../baseella-ui-components/src';
import api from '../../../api/api';
import {
  ExternalAccount,
  ExternalAccountFormValues,
  ExternalAccountTopUpOption,
  ExternalAccountTopUpOptionFormValues,
  ExternalAccountWithIntegrationFormValues,
  LinkedClient,
  LinkedClientsFormValues,
  Organization,
  PspIntegration,
  PspIntegrationOrganization,
  PspPartner,
} from '../types';
import { CASP } from '../../../../../baseella-ui-components/src/components/transactions-sidepanels/types';
import { Account } from '../../accounts/types';

export const externalAccountsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAccountsLinkedClients: build.query<Response<LinkedClient>, string>({
      query: (query) => ({
        url: `/v1/psp-integrations-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    bindAccountsExternalOrganization: build.mutation<
      {
        data: LinkedClient;
      },
      LinkedClientsFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-organizations',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsPspIntegrationsOrganizationsByName: build.query<
      { data: PspIntegrationOrganization[] },
      string
    >({
      query: (query) => ({
        url: `/v1/psp-integrations-organizations/search/external-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsOrganizations: build.query<Response<Organization>, string>({
      query: (query) => ({
        url: `/v1/clients-organizations?take=40${query ? `&${query}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExternalAccountChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExternalAccountTopUpOptions: build.query<
      Response<ExternalAccountTopUpOption>,
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations-payment-accounts?pspIntegrationAccountId=${data.id}&all=true${data.query ? data.query : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createExternalAccountWithIntegration: build.mutation<
      {
        data: ExternalAccount;
      },
      ExternalAccountWithIntegrationFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/generate-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrations: build.query<Response<PspIntegration>, void>({
      query: () => ({
        url: '/v1/psp-integrations',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsCASP: build.query<Response<CASP>, void>({
      query: () => ({
        url: '/v1/casps',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExternalAccountStatus: build.mutation<
      { data: { count: number } },
      { ids: string[]; active: boolean }
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/status',
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createExternalAccount: build.mutation<
      {
        data: ExternalAccount;
      },
      ExternalAccountFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsPspPartners: build.query<Response<PspPartner>, void>({
      query: () => ({
        url: '/v1/psp-partners?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsForExternal: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExternalAccountCurrencies: build.mutation<
      { data: ExternalAccount },
      { id: string; currenciesIds: string[] }
    >({
      query: (body) => ({
        url: `/v1/psp-integrations-accounts/${body.id}`,
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExternalAccount: build.query<{ data: ExternalAccount }, string>({
      query: (id) => ({
        url: `/v1/psp-integrations-accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExternalAccountsList: build.query<Response<ExternalAccount>, string>({
      query: (query) => ({
        url: `/v1/psp-integrations-accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deletePaymentAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/psp-integrations-payment-accounts/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createPaymentsAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      ExternalAccountTopUpOptionFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-payment-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePaymentsAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      { id: string; body: ExternalAccountTopUpOptionFormValues }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations-payment-accounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetAccountsLinkedClientsQuery,
  useBindAccountsExternalOrganizationMutation,
  useLazyGetAccountsPspIntegrationsOrganizationsByNameQuery,
  useLazyGetAccountsOrganizationsQuery,
  useLazyGetExternalAccountChangeLogsQuery,
  useLazyGetExternalAccountTopUpOptionsQuery,
  useCreateExternalAccountWithIntegrationMutation,
  useLazyGetPspIntegrationsQuery,
  useLazyGetAccountsCASPQuery,
  useUpdateExternalAccountStatusMutation,
  useCreateExternalAccountMutation,
  useLazyGetAccountsPspPartnersQuery,
  useLazyGetAccountsForExternalQuery,
  useUpdateExternalAccountCurrenciesMutation,
  useLazyGetExternalAccountQuery,
  useLazyGetExternalAccountsListQuery,
  useDeletePaymentAccountsMutation,
  useCreatePaymentsAccountsMutation,
  useUpdatePaymentsAccountsMutation,
} = externalAccountsApi;

export const {
  endpoints: {
    getAccountsLinkedClients,
    bindAccountsExternalOrganization,
    getAccountsPspIntegrationsOrganizationsByName,
    getAccountsOrganizations,
    getExternalAccountChangeLogs,
    getExternalAccountTopUpOptions,
    createExternalAccountWithIntegration,
    getPspIntegrations,
    getAccountsCASP,
    updateExternalAccountStatus,
    createExternalAccount,
    getAccountsPspPartners,
    getAccountsForExternal,
    updateExternalAccountCurrencies,
    getExternalAccount,
    getExternalAccountsList,
    deletePaymentAccounts,
    createPaymentsAccounts,
    updatePaymentsAccounts,
  },
} = externalAccountsApi;
